import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { Button } from '../../Button/Button.tsx';
import { RIGHT_PANEL_STYLES, SCROLLABLE_CONTAINER_STYLES } from '../constants.ts';
import type { RightPanelConfig, SelectedItemRenderProps } from '../types.ts';

type RightPanelProps<TSelected> = RightPanelConfig<TSelected> & {
  renderSelectedItem: (props: SelectedItemRenderProps<TSelected>) => React.ReactNode;
};

export const RightPanel = <TSelected,>({
  title,
  items,
  onItemRemove,
  getItemId,
  onRemoveAll,
  emptyStateMessage,
  headerActions,
  selectedCount,
  pendoFeature,
  renderSelectedItem,
}: RightPanelProps<TSelected>) => {
  const { t } = useTranslation();

  const isEmpty = items.length === 0;
  const defaultEmptyMessage = emptyStateMessage ?? t('No items selected');
  const count = selectedCount !== undefined ? selectedCount : items.length;

  return (
    <Box data-test="selection-drawer-right-panel" data-pendo-feature={pendoFeature} sx={RIGHT_PANEL_STYLES}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 2,
        }}
      >
        <Typography variant="body1Bold" component="h6" data-test="right-panel-title">
          {title}
          {count > 0 && ` [ ${count} ]`}
        </Typography>

        {headerActions}

        {onRemoveAll && (
          <Button
            data-test="clear-all-button"
            onClick={onRemoveAll}
            disabled={count === 0}
            variant="text"
            color="error"
            size="small"
            sx={{ ml: 'auto' }}
          >
            {t('Clear All')}
          </Button>
        )}
      </Box>

      <Box sx={SCROLLABLE_CONTAINER_STYLES} data-test="selected-items-list">
        {isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              px: 2,
            }}
          >
            <Typography
              variant="body2"
              data-test="right-panel-empty-state"
              sx={{
                color: 'text.secondary',
              }}
            >
              {defaultEmptyMessage}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ px: 2, py: 2 }}>
            {items.map(item => {
              const itemId = getItemId(item);

              return (
                <Box key={itemId} data-test={`selected-item-${itemId}`}>
                  {renderSelectedItem({ item, onRemove: onItemRemove })}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};
